import { createRouter, createWebHistory } from '@ionic/vue-router';
import { isPlatform } from '@ionic/vue';
import { RouteRecordRaw } from 'vue-router';
import common from '@/common';
import audioPreloader from '@/utils/audioPreloader';

const PUBLIC_ROUTES = new Set([
  'Demo',
  'Launch',
  'LoginPrepare',
  'Login',
  'Register',
  'RegisterName',
  'SetPasswordByName',
  'About',
  'ForgetPassword',
  'ForgetPasswordByMobile',
  'ForgetPasswordByName',
  'SetPassword',
  'MobileVerify',
  'ResetPasswordByMobile',
  'ResetPasswordByName',
]);

const FLOW_ROUTES = new Set([
  'TestPrepare',
  'Test',
  'TestFinish',
  'TestResult',
  'FollowUp',
  'SurveyPrepare',
  'Survey',
  'SurveyFinish',
  'NgoDataEntry',
  'Credit',
]);

const FLOW_PREREQUISITE: Record<string, string> = {
  Test:          'test_data',
  TestFinish:    'storage_test_id',
  TestResult:    'storage_test_id',
  FollowUp:      'storage_test_id',
  SurveyPrepare: 'storage_test_id',
  Survey:        'storage_test_id',
  SurveyFinish:  'storage_test_id',
  NgoDataEntry:  'storage_test_id',
};

const ensureAudioPreloaded = async (to: any, from: any, next: any) => {
  if (isPlatform('hybrid') || audioPreloader.isComplete()) {
    next();
    return;
  }
  
  const loading = common.showLoading();
  const loadingInstance = await loading;
  
  const updateProgress = () => {
    const { loaded, total } = audioPreloader.getProgress();
    loadingInstance.message = `加載音頻文件中 ${loaded}/${total}`;
  };
  
  const interval = setInterval(updateProgress, 100);
  await audioPreloader.preloadAll();
  clearInterval(interval);
  common.hideLoading(loading);
  next();
};

const routes: Array<RouteRecordRaw> = [
  {
    path: '/demo',
    name: 'Demo',
    component: () => import('../views/Demo.vue')
  },
  {
    path: '/',
    redirect: '/launch'
  },
  {
    path: '/launch',
    name: 'Launch',
    component: () => import('../views/Launch.vue')
  },
  {
    path: '/home',
    name: 'Home',
    component: () => import('../views/Home.vue')
  },
  {
    path: '/loginPrepare',
    name: 'LoginPrepare',
    component: () => import('../views/LoginPrepare.vue')
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue')
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('../views/Register.vue')
  },
  {
    path: '/registerName/:mobile',
    name: 'RegisterName',
    component: () => import('../views/RegisterName.vue')
  },
  {
    path: '/setPasswordByName/:mobile/:name',
    name: 'SetPasswordByName',
    component: () => import('../views/SetPasswordByName.vue')
  },
  {
    path: '/about/:back_url',
    name: 'About',
    component: () => import('../views/About.vue')
  },
  {
    path: '/testPrepare',
    name: 'TestPrepare',
    component: () => import('../views/TestPrepare.vue'),
    beforeEnter: ensureAudioPreloaded
  },
  {
    path: '/test',
    name: 'Test',
    component: () => import('../views/Test.vue'),
    beforeEnter: ensureAudioPreloaded
  },
  {
    path: '/testFinish',
    name: 'TestFinish',
    component: () => import('../views/TestFinish.vue'),
    beforeEnter: ensureAudioPreloaded
  },
  {
    path: '/testResult',
    name: 'TestResult',
    component: () => import('../views/TestResult.vue'),
    beforeEnter: ensureAudioPreloaded
  },
  {
    path: '/followUp',
    name: 'FollowUp',
    component: () => import('../views/FollowUp.vue'),
    beforeEnter: ensureAudioPreloaded
  },
  {
    path: '/forgetPassword',
    name: 'ForgetPassword',
    component: () => import('../views/ForgetPassword.vue')
  },
  {
    path: '/forgetPasswordByMobile',
    name: 'ForgetPasswordByMobile',
    component: () => import('../views/ForgetPasswordByMobile.vue')
  },
  {
    path: '/forgetPasswordByName',
    name: 'ForgetPasswordByName',
    component: () => import('../views/ForgetPasswordByName.vue')
  },
  {
    path: '/setPassword/:mobile',
    name: 'SetPassword',
    component: () => import('../views/SetPassword.vue')
  },
  {
    path: '/mobileVerify/:mobile/:type',
    name: 'MobileVerify',
    component: () => import('../views/MobileVerify.vue')
  },
  {
    path: '/resetPasswordByMobile/:mobile',
    name: 'ResetPasswordByMobile',
    component: () => import('../views/ResetPasswordByMobile.vue')
  },
  {
    path: '/resetPasswordByName/:mobile/:name',
    name: 'ResetPasswordByName',
    component: () => import('../views/ResetPasswordByName.vue')
  },
  {
    path: '/surveyPrepare/:type',
    name: 'SurveyPrepare',
    component: () => import('../views/SurveyPrepare.vue'),
    beforeEnter: ensureAudioPreloaded
  },
  {
    path: '/survey',
    name: 'Survey',
    component: () => import('../views/Survey.vue'),
    beforeEnter: ensureAudioPreloaded
  },
  {
    path: '/surveyFinish',
    name: 'SurveyFinish',
    component: () => import('../views/SurveyFinish.vue'),
    beforeEnter: ensureAudioPreloaded
  },
  {
    path: '/ngoDataEntry',
    name: 'NgoDataEntry',
    component: () => import('../views/NgoDataEntry.vue'),
    beforeEnter: ensureAudioPreloaded
  },
  {
    path: '/credit',
    name: 'Credit',
    component: () => import('../views/Credit.vue'),
    beforeEnter: ensureAudioPreloaded
  },
  {
    path: '/deleteAccount',
    name: 'DeleteAccount',
    component: () => import('../views/DeleteAccount.vue')
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(async (to) => {
  if (!FLOW_ROUTES.has(to.name as string)) {
    const hasFlowData = (await common.getStorage('storage_test_id'))
      || (await common.getStorage('test_data'))
      || (await common.getStorage('user_info'));
    if (hasFlowData) {
      await common.clearFlow();
    }
  }

  if (PUBLIC_ROUTES.has(to.name as string)) return true;
  const token = await common.getToken();
  if (!token) return { name: 'Launch' };

  const prereq = FLOW_PREREQUISITE[to.name as string];
  if (prereq) {
    const value = await common.getStorage(prereq);
    if (value == null) return { name: 'Home' };
  }
  return true;
})

export default router
